import React, { useCallback, useEffect, useState } from 'react';
import API_BASE from '../../utils/api.js';
import { useAuth } from '../../auth/useAuth';
import UpgradePremiumModal from '../Stripe/UpgradePremiumModal';
import DowngradeToStandardModal from '../Stripe/DowngradeToStandardModal';
import { SEAT_PRICE_LABEL, PREMIUM_INCLUDED_SEATS } from '../../utils/seatPricing';

const PLAN_COPY = {
  standard: {
    label: 'Standard',
    price: '$75/mo',
    priceAnnual: '$810/yr',
    features: ['Core project & proposal tools', 'Client Portal', 'Reports'],
    seatFeature: (seatPrice) => `Additional team seats at ${seatPrice} each`,
  },
  premium: {
    label: 'Premium',
    price: '$125/mo',
    priceAnnual: '$1350/yr',
    features: ['Everything in Standard', 'Time Billing', 'QuickBooks integration'],
    seatFeature: (seatPrice) =>
      `${PREMIUM_INCLUDED_SEATS} team seats included, then ${seatPrice} each`,
  },
};

export default function BillingSettings() {
  const apiUrl = API_BASE;
  const { auth } = useAuth();
  const userId = localStorage.getItem('userId');

  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState('monthly');
  const [linkedAccountOwnerId, setLinkedAccountOwnerId] = useState('');
  const [activeSeatCount, setActiveSeatCount] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!userId) return;
    const res = await fetch(`${apiUrl}/api/settings/${userId}`);
    const data = await res.json();

    setBilling(data.billing === 'annual' ? 'annual' : 'monthly');
    setLinkedAccountOwnerId(data.linkedAccountOwnerId || '');
    const seats = Array.isArray(data.additionalSeats) ? data.additionalSeats : [];
    setActiveSeatCount(seats.filter((s) => s.status === 'pending' || s.status === 'claimed').length);
    setLoading(false);
  }, [apiUrl, userId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  if (loading) {
    return (
      <div className="qb-settings-page">
        <h2 className="qb-title">Billing</h2>
        <div className="qb-card">
          <div className="qb-left">
            <div className="qb-skeleton">
              <div className="sk-row sk-title"></div>
              <div className="sk-row sk-line"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (linkedAccountOwnerId) {
    return (
      <div className="qb-settings-page">
        <h2 className="qb-title">Billing</h2>
        <div className="qb-card">
          <div className="qb-left">
            <p className="qb-info" style={{ marginBottom: 0 }}>
              Billing is managed by your account owner.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const plan = auth?.plan === 'premium' ? 'premium' : 'standard';
  const downgradeBlocked = plan === 'premium' && activeSeatCount > 0;

  return (
    <div className="qb-settings-page">
      <h2 className="qb-title">Billing</h2>
      <p className="qb-subtitle">Manage your InteriorFolio subscription plan.</p>

      <div className="qb-card">
        <div className="qb-left">
          <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Current Plan</h3>
          <p className="qb-info" style={{ marginBottom: 0 }}>
            {PLAN_COPY[plan].label} — billed {billing === 'annual' ? 'annually' : 'monthly'}
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, maxWidth: 640 }}>
        {['standard', 'premium'].map((key) => {
          const copy = PLAN_COPY[key];
          const isCurrent = key === plan;
          return (
            <div key={key} className="qb-card" style={{ flexDirection: 'column', alignItems: 'flex-start', maxWidth: 'none' }}>
              <div className="qb-left" style={{ width: '100%' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>{copy.label}</h3>
                <p className="qb-info" style={{ marginBottom: 10 }}>
                  {billing === 'annual' ? copy.priceAnnual : copy.price}
                </p>
                <ul style={{ margin: '0 0 16px', paddingLeft: 18, fontSize: '0.9rem', color: '#4b5563' }}>
                  {[...copy.features, copy.seatFeature(SEAT_PRICE_LABEL[billing])].map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>

                {isCurrent ? (
                  <button className="qb-button" style={{ background: '#e5e7eb', color: '#6b7280', cursor: 'default' }} disabled>
                    Current Plan
                  </button>
                ) : (
                  <>
                    <button
                      className="qb-button connect"
                      disabled={key === 'standard' && downgradeBlocked}
                      onClick={() => (key === 'premium' ? setShowUpgradeModal(true) : setShowDowngradeModal(true))}
                    >
                      Switch to {copy.label}
                    </button>
                    {key === 'standard' && downgradeBlocked && (
                      <p className="qb-info" style={{ marginTop: 8, marginBottom: 0, fontSize: '0.8rem', color: '#b91c1c' }}>
                        Remove your team's additional seats in My Team before downgrading.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showUpgradeModal && <UpgradePremiumModal onClose={() => setShowUpgradeModal(false)} />}
      {showDowngradeModal && <DowngradeToStandardModal onClose={() => setShowDowngradeModal(false)} />}
    </div>
  );
}
